import type { PropsWithChildren, ReactElement } from "react";
import { useSyncExternalStore } from "react";

import {
  logger,
  normalizeLocation,
  type ImportMetaLocation,
} from "@-/dev.improve.haniverse.lib.logger";
import {
  ReactNativeErrorBoundary,
  type FallbackComponentProps,
} from "@-/com.npmjs.react-native-error-boundary";
import { BoundedSuspense } from "#bounded-suspense";
import { SuspensePageErrorFallback } from "#suspense-page-error-fallback";
import { SuspensePageFallback } from "#suspense-page-fallback";

function subscribeToHydrationState(): () => void {
  return () => {
    // no-op: the server/client distinction never changes after hydration
  };
}

function getClientHydrationSnapshot(): boolean {
  return false;
}

function getServerHydrationSnapshot(): boolean {
  return true;
}

/**
 * `true` during server rendering and the matching first client hydration pass, then `false` after
 * hydration commits. A `window` sniff would return different values for the server and that first
 * client render, causing React to discard the server placeholder before the query can resolve.
 */
function useIsServerOrHydrating(): boolean {
  return useSyncExternalStore(
    subscribeToHydrationState,
    getClientHydrationSnapshot,
    getServerHydrationSnapshot,
  );
}

export function SuspensePageBoundary({
  children,
  fallback,
  location,
  onErrorLayout,
  suspendsOnServer = true,
}: PropsWithChildren<{
  // Every page owns its loading geometry. Missing/null/boolean fallbacks are compile errors.
  fallback: ReactElement;
  location: ImportMetaLocation;
  // A prepared native route may reveal a laid-out error page, but never a blank underlay.
  onErrorLayout?: () => void;
  // Whether `children` actually suspend during web server rendering (`EXPO_WEB_OUTPUT=server`).
  // Page-content boundaries wrap a `usePrefetchedSuspenseQuery` that ALWAYS suspends on the server
  // (its data lives in an unreachable on-device DB), so the default `true` renders the placeholder
  // there to keep `renderToString` from aborting the shell. Set `false` for boundaries that only
  // wrap non-suspending work AND host context providers the subtree depends on (e.g. the app-layout
  // boundary around `AppLayoutSuspense`/`AppLayoutContextProvider`): rendering the placeholder there
  // would drop those providers from the server HTML and cause a hydration mismatch, so those must
  // render `children` on the server as usual.
  suspendsOnServer?: boolean;
}>) {
  const scopedLogger = logger(location);
  const operation = `page load ${normalizeLocation(location)}`;
  const isServerOrHydrating = useIsServerOrHydrating();

  // Skip guaranteed-to-suspend page data on the server so Expo's `renderToString` keeps the full
  // shell. The first hydrating client render deliberately returns the SAME placeholder; React then
  // switches to the live Suspense tree immediately after hydration instead of rejecting mismatched
  // HTML. The required page-shaped skeleton is shared by hydration and client suspension.
  if (suspendsOnServer && isServerOrHydrating) {
    return fallback;
  }

  return (
    <ReactNativeErrorBoundary
      FallbackComponent={({ error, resetError }: FallbackComponentProps) => (
        <SuspensePageErrorFallback
          error={error}
          loadingFallback={fallback}
          logger={scopedLogger}
          onLayout={onErrorLayout}
          resetError={resetError}
        />
      )}
    >
      {/* Bounded: a page that stays suspended past `SUSPENSE_DEADLINE_MS` throws into the error
          boundary just above, so a promise that never settles ends on the error screen (retry,
          return home, restart) instead of a skeleton that never leaves. */}
      <BoundedSuspense
        fallback={<SuspensePageFallback operation={operation}>{fallback}</SuspensePageFallback>}
        operation={operation}
      >
        {children}
      </BoundedSuspense>
    </ReactNativeErrorBoundary>
  );
}
